import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, CreditCard, Banknote, Check, Loader2, PartyPopper, ShieldCheck, Info } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentChoice() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [method, setMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  const sessionId = searchParams.get('session_id');

  const pollStatus = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 5) { toast.error('Timeout verifica pagamento'); return; }
    try {
      const res = await api.get(`/payments/status/${sid}`);
      if (res.data.payment_status === 'paid') {
        setPaymentComplete(true);
        toast.success('Garanzia registrata!');
        return;
      }
      if (res.data.status === 'expired') { toast.error('Sessione scaduta'); return; }
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    } catch { toast.error('Errore verifica'); }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [qRes, aRes] = await Promise.all([
          api.get(`/quotes/${quoteId}`),
          api.get('/appointments')
        ]);
        setQuote(qRes.data);
        const apt = aRes.data.find((a) => a.quote_id === quoteId);
        setAppointment(apt);
        if (apt?.payment_status === 'paid') setPaymentComplete(true);
      } catch { toast.error('Errore caricamento'); }
      finally { setLoading(false); }
    };
    load();
  }, [quoteId]);

  useEffect(() => {
    if (sessionId) pollStatus(sessionId);
  }, [sessionId, pollStatus]);

  const handleCardGuarantee = async () => {
    setProcessing(true);
    try {
      const res = await api.post('/payments/checkout', {
        quote_id: quoteId,
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch {
      toast.error('Errore nel collegamento carta');
      setProcessing(false);
    }
  };

  const handlePayAtTechnician = async () => {
    if (!appointment) return;
    setProcessing(true);
    try {
      await api.put(`/appointments/${appointment.appointment_id}/payment-method`, { method: 'on_site' });
      setPaymentComplete(true);
      toast.success('Prenotazione confermata!');
    } catch { toast.error('Errore'); }
    finally { setProcessing(false); }
  };

  const handleConfirm = () => {
    if (!method) { toast.error('Scegli come vuoi procedere'); return; }
    if (method === 'card_guarantee') handleCardGuarantee();
    else handlePayAtTechnician();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center" data-testid="payment-success">
        <Card className="border-0 shadow-sm rounded-xl max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PartyPopper className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-extrabold text-slate-900 mb-2">Prenotazione Confermata!</h2>
            <p className="text-sm text-slate-500 mb-6">
              Il tuo appuntamento è stato confermato.
              {appointment && (
                <> Data: {new Date(appointment.date).toLocaleDateString('it-IT')} alle {appointment.time_slot}</>
              )}
            </p>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
              onClick={() => navigate('/appuntamenti')}
              data-testid="go-appointments-btn"
            >
              Vedi Appuntamenti
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const avgPrice = quote ? ((quote.price_min + quote.price_max) / 2).toFixed(0) : '0';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="payment-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="payment-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <p className="text-sm font-bold text-slate-900">Conferma Prenotazione</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Summary */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-6">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Riepilogo</p>
            {appointment && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-slate-600">Appuntamento</span>
                <span className="text-sm font-semibold text-slate-900">
                  {new Date(appointment.date).toLocaleDateString('it-IT')} - {appointment.time_slot}
                </span>
              </div>
            )}
            <Separator className="my-3" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Importo stimato</span>
              <span className="text-lg font-extrabold text-slate-900">{avgPrice} EUR</span>
            </div>
            {quote?.price_min !== quote?.price_max && (
              <p className="text-xs text-slate-400 mt-1">(da {quote?.price_min} a {quote?.price_max} EUR)</p>
            )}
          </CardContent>
        </Card>

        {/* Title */}
        <div>
          <h3 className="text-base font-extrabold text-slate-900 mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Come vuoi procedere?
          </h3>
          <p className="text-xs text-slate-500">Il pagamento del servizio avviene sempre al tecnico, a fine intervento.</p>
        </div>

        {/* Option 1: Card Guarantee */}
        <Card
          className={`border-2 shadow-sm rounded-xl cursor-pointer transition-all ${method === 'card_guarantee' ? 'border-blue-500 bg-blue-50/30' : 'border-slate-100 hover:border-slate-200'}`}
          onClick={() => setMethod('card_guarantee')}
          data-testid="method-card-guarantee"
        >
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${method === 'card_guarantee' ? 'border-blue-500' : 'border-slate-300'}`}>
                {method === 'card_guarantee' && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <span className="font-bold text-sm text-slate-900">Lascia una garanzia con carta</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed mb-3">
                  Inserisci la tua carta come garanzia di prenotazione.
                  <strong> Non viene addebitato nulla</strong> — è solo una pre-autorizzazione
                  che verrà rilasciata automaticamente dopo l'intervento.
                </p>
                <div className="bg-blue-50 rounded-lg p-3 flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-blue-700 leading-relaxed">
                    <strong>Nessun addebito.</strong> La tua carta viene solo verificata.
                    Paghi il tecnico direttamente a fine lavoro (contanti, POS o bonifico).
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Option 2: Pay at Technician */}
        <Card
          className={`border-2 shadow-sm rounded-xl cursor-pointer transition-all ${method === 'pay_technician' ? 'border-blue-500 bg-blue-50/30' : 'border-slate-100 hover:border-slate-200'}`}
          onClick={() => setMethod('pay_technician')}
          data-testid="method-pay-technician"
        >
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${method === 'pay_technician' ? 'border-blue-500' : 'border-slate-300'}`}>
                {method === 'pay_technician' && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Banknote className="w-4 h-4 text-emerald-600" />
                  <span className="font-bold text-sm text-slate-900">Prenota senza carta</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed mb-3">
                  Prenota liberamente senza inserire nessun dato di pagamento.
                  Paghi direttamente al tecnico quando viene a casa tua.
                </p>
                <div className="bg-emerald-50 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-emerald-700 leading-relaxed">
                    Accettiamo <strong>contanti, POS e bonifico</strong>. Il tecnico porta il POS con sé.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button
          className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 text-base font-semibold"
          onClick={handleConfirm}
          disabled={processing || !method}
          data-testid="confirm-payment-btn"
        >
          {processing ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Elaborazione...</>
          ) : method === 'card_guarantee' ? (
            <><ShieldCheck className="w-4 h-4 mr-2" /> Procedi con Garanzia Carta</>
          ) : (
            <><Check className="w-4 h-4 mr-2" /> Conferma Prenotazione</>
          )}
        </Button>
      </main>
    </div>
  );
}
